import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSession } from "../../session/SessionContext";
import { useToast } from "../../components/toast/toastContext";
import { ErrorState, LoadingState, PermissionDeniedState } from "../../components/states";
import { DormancyCandidatesSection } from "../../components/unused/DormancyCandidatesSection";
import { UnattributedConsumptionSection } from "../../components/unused/UnattributedConsumptionSection";
import { UnusedSeatBaselineSection } from "../../components/unused/UnusedSeatBaselineSection";
import { AccountMatchingSections } from "./AccountMatchingSections";
import {
  captureUnusedSeatBaseline,
  downloadCsvExport,
  fetchUnattributedConsumption,
  fetchUnusedEntitlement,
  fetchUnusedSeatBaseline,
  type UnattributedConsumptionResponse,
  type UnusedEntitlementResponse,
  type UnusedSeatBaselineSnapshotResponse,
} from "../../api/organisationReporting";

const AccountsAdministerPermission = "accounts.administer";

type CandidatesState =
  | { kind: "loading" }
  | { kind: "loaded"; response: UnusedEntitlementResponse }
  | { kind: "error"; message: string };

type UnattributedState =
  | { kind: "loading" }
  | { kind: "loaded"; response: UnattributedConsumptionResponse }
  | { kind: "error"; message: string };

type BaselineState = { kind: "loading" } | { kind: "loaded"; snapshots: UnusedSeatBaselineSnapshotResponse[] } | { kind: "error"; message: string };

/**
 * System's Accounts tab (phase 14, `AIA-81`, `SC-2`, `SC-9`, formerly the Unused entitlement and Account matching pages): seats that
 * could be given up, with their evidence, and people who cannot be assessed (never counted as unused); the standalone report of spend
 * no person carries (`TD-19`); unused seats over time (`SC-9`); and, at the foot, account matching. The whole tab, and every endpoint it
 * calls, is gated on `accounts.administer`: seeing these reports is part of administering accounts, and holding `consumption.read.*` does
 * not open them. The tab shows named individuals' usage, so the permission is granted deliberately (`QR-06`).
 */
export function AccountsContent(): React.JSX.Element {
  const { t } = useTranslation();
  const { snapshot } = useSession();
  const held = snapshot.status === "signed-in" ? snapshot.permissions : [];

  if (!held.includes(AccountsAdministerPermission)) {
    return <PermissionDeniedState title={t("unusedEntitlement.denied.title")} description={t("unusedEntitlement.denied.description")} />;
  }

  return (
    <div>
      <CandidatesAndUnattributed canDecide />
      <Baseline canDecide />
      <AccountMatchingSections />
    </div>
  );
}

function CandidatesAndUnattributed({ canDecide }: { canDecide: boolean }): React.JSX.Element {
  const { t } = useTranslation();
  const toast = useToast();
  const [candidatesState, setCandidatesState] = useState<CandidatesState>({ kind: "loading" });
  const [unattributedState, setUnattributedState] = useState<UnattributedState>({ kind: "loading" });
  const [exportPending, setExportPending] = useState(false);

  const loadCandidates = useCallback(async () => {
    setCandidatesState({ kind: "loading" });
    const result = await fetchUnusedEntitlement();
    if (result.kind === "ok") {
      setCandidatesState({ kind: "loaded", response: result.response });
    } else if (result.kind === "denied") {
      setCandidatesState({ kind: "error", message: t("unusedEntitlement.denied.description") });
    } else if (result.kind === "aborted") {
      return;
    } else {
      setCandidatesState({ kind: "error", message: result.message });
    }
  }, [t]);

  const loadUnattributed = useCallback(async () => {
    setUnattributedState({ kind: "loading" });
    const result = await fetchUnattributedConsumption();
    if (result.kind === "ok") {
      setUnattributedState({ kind: "loaded", response: result.response });
    } else if (result.kind === "denied") {
      setUnattributedState({ kind: "error", message: t("unattributed.denied") });
    } else if (result.kind === "aborted") {
      return;
    } else {
      setUnattributedState({ kind: "error", message: result.message });
    }
  }, [t]);

  useEffect(() => {
    void loadCandidates();
    void loadUnattributed();
  }, [loadCandidates, loadUnattributed]);

  const handleExport = (kind: "unused-entitlement" | "unattributed") => {
    setExportPending(true);
    void downloadCsvExport(kind).then((result) => {
      setExportPending(false);
      if (result.kind === "denied") {
        toast.show(t("organisation.exportDenied"), "error");
      } else if (result.kind === "error") {
        toast.show(result.message, "error");
      }
    });
  };

  const exportButton = (kind: "unused-entitlement" | "unattributed", label: string) => (
    <button type="button" className="button button-secondary" disabled={exportPending} onClick={() => handleExport(kind)}>
      {label}
    </button>
  );

  return (
    <>
      {candidatesState.kind === "loading" && <LoadingState label={t("unusedEntitlement.loading")} />}
      {candidatesState.kind === "error" && (
        <ErrorState title={t("unusedEntitlement.errorTitle")} description={candidatesState.message} onRetry={() => void loadCandidates()} />
      )}
      {candidatesState.kind === "loaded" && (
        <DormancyCandidatesSection
          candidates={candidatesState.response.candidates}
          unknown={candidatesState.response.unknown}
          thresholdDays={candidatesState.response.thresholdDays}
          canDecide={canDecide}
          exportAction={exportButton("unused-entitlement", t("unusedEntitlement.exportCandidatesCsv"))}
        />
      )}

      {unattributedState.kind === "loading" && <LoadingState label={t("unattributed.loading")} />}
      {unattributedState.kind === "error" && (
        <ErrorState title={t("unattributed.errorTitle")} description={unattributedState.message} onRetry={() => void loadUnattributed()} />
      )}
      {unattributedState.kind === "loaded" && (
        <UnattributedConsumptionSection entries={unattributedState.response.entries} exportAction={exportButton("unattributed", t("unusedEntitlement.exportUnattributedCsv"))} />
      )}
    </>
  );
}

function Baseline({ canDecide }: { canDecide: boolean }): React.JSX.Element {
  const { t } = useTranslation();
  const toast = useToast();
  const [state, setState] = useState<BaselineState>({ kind: "loading" });
  const [capturing, setCapturing] = useState(false);

  const load = useCallback(async () => {
    setState({ kind: "loading" });
    const result = await fetchUnusedSeatBaseline();
    if (result.kind === "ok") {
      setState({ kind: "loaded", snapshots: result.response });
    } else if (result.kind === "denied") {
      setState({ kind: "error", message: t("unusedEntitlement.denied.description") });
    } else if (result.kind === "aborted") {
      return;
    } else {
      setState({ kind: "error", message: result.message });
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleCapture = () => {
    setCapturing(true);
    void captureUnusedSeatBaseline().then((result) => {
      setCapturing(false);
      if (result.kind === "ok") {
        toast.show(t("unusedEntitlement.baseline.captured"), "success");
        void load();
      } else if (result.kind === "denied") {
        toast.show(t("unusedEntitlement.baseline.captureDenied"), "error");
      } else if (result.kind === "error") {
        toast.show(t("unusedEntitlement.baseline.captureFailed", { message: result.message }), "error");
      }
    });
  };

  const handleExport = () => {
    void downloadCsvExport("unused-seat-baseline").then((result) => {
      if (result.kind === "denied") {
        toast.show(t("organisation.exportDenied"), "error");
      } else if (result.kind === "error") {
        toast.show(result.message, "error");
      }
    });
  };

  if (state.kind === "loading") {
    return <LoadingState label={t("unusedEntitlement.baseline.loading")} />;
  }

  if (state.kind === "error") {
    return <ErrorState title={t("unusedEntitlement.baseline.errorTitle")} description={state.message} onRetry={() => void load()} />;
  }

  return (
    <UnusedSeatBaselineSection
      snapshots={state.snapshots}
      canCapture={canDecide}
      capturing={capturing}
      onCapture={handleCapture}
      exportAction={
        <button type="button" className="button button-secondary" onClick={handleExport}>
          {t("unusedEntitlement.baseline.exportCsv")}
        </button>
      }
    />
  );
}
